import moment from 'moment';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { ConfigConstants } from '../../config/config-constants';
import { ODataObject, ODataValue } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { ArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import { EquipmentModel } from './equipment.model';
import { TimeZoneModel } from './time-zone.model';

export class CseSalesTidInputModel extends CompositeModel {
    public frontId: TextModel;
    public techIdentNumber: TextModel;
    public functionalLoc: TextModel;
    public material: TextModel;
    public serialNo: TextModel;
    public frontDate: DateModel;
    public frontTime: TimeModel;
    public equipment: TextModel;
    public emailFaxSet: ArrayModel<TextModel>;
    public notiCreateEquipmentsSet: ArrayModel<EquipmentModel>;

    private timeZone: TimeZoneModel;
    public constructor() {
        super('CseSalesTidInputModel');
        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigInstanceToken);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);

        this.timeZone = new TimeZoneModel();

        // setup child models
        const now = moment();
        this.frontId = <TextModel>this.set(new TextModel('FrontId', configService.getUserFrontId()));

        this.techIdentNumber = <TextModel>this.set(new TextModel('TechnicalIdentNo'));
        modelOptionsLoader.load(this.techIdentNumber, 'CseSalesTidInput', 'TechnicalIdentNo');

        this.functionalLoc = <TextModel>this.set(new TextModel('FunctionalLoc'));
        modelOptionsLoader.load(this.functionalLoc, 'CseSalesTidInput', 'FunctionalLoc');

        this.material = <TextModel>this.set(new TextModel('Material'));
        modelOptionsLoader.load(this.material, 'CseSalesTidInput', 'Material');
        this.material.options = {
            ...this.material.options,
            applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
        };

        this.serialNo = <TextModel>this.set(new TextModel('SerialNo'));
        modelOptionsLoader.load(this.serialNo, 'CseSalesTidInput', 'SerialNo');
        this.serialNo.options = {
            ...this.serialNo.options,
            applicationIdentifier: ConfigConstants.ApplicationIdentifier.SerialNumber,
        };

        this.frontDate = <DateModel>this.set(new DateModel('FrontDate', now));

        this.frontTime = <TimeModel>(
            this.set(
                new TimeModel(
                    'FrontTime',
                    now,
                    new TimeModelOptions({ format: ConfigConstants.formatXSDTimeDuration }),
                ),
            )
        );
        this.equipment = <TextModel>this.set(new TextModel('EquipmentNum'));

        this.emailFaxSet = <ArrayModel<TextModel>>this.set(new ArrayModel<TextModel>('EmailFaxSet'));
        this.notiCreateEquipmentsSet = <ArrayModel<EquipmentModel>>(
            this.set(new ArrayModel<EquipmentModel>('NotiCreateEquipmentsSet'))
        );

        this.resetRequiredOption();
    }

    public resetRequiredOption(): void {
        this.techIdentNumber = assignModelOptions(
            this.techIdentNumber,
            {
                ...this.techIdentNumber.options,
                required: !this.functionalLoc.value && !this.material.value && !this.serialNo.value,
            },
            this,
        );
        this.functionalLoc = assignModelOptions(
            this.functionalLoc,
            {
                ...this.functionalLoc.options,
                required: !this.techIdentNumber.value && !this.material.value && !this.serialNo.value,
            },
            this,
        );
        this.material = assignModelOptions(
            this.material,
            {
                ...this.material.options,
                required: !this.techIdentNumber.value && !this.functionalLoc.value,
            },
            this,
        );

        this.serialNo = assignModelOptions(
            this.serialNo,
            {
                ...this.serialNo.options,
                required: !this.techIdentNumber.value && !this.functionalLoc.value,
            },
            this,
        );
        this.setReadOnlyOptions();
    }

    /*
     * This method set's readonly property for the fields as per below conditions
     *
     *   if "Techn. Ident" is filled out then "Functional Loc", "Material number", "Serial number" shall be disabled.
     *
     *   if "Functional Loc" is filled out then "Techn. Ident", "Material number", "Serial number" shall be disabled.
     *
     *   if "Material number" is filled out then "Techn. Ident" and "Functional Loc" shall be disabled
     *   and "Serial number" shall be mandatory.
     *
     *   if "Serial number" is filled out then "Techn. Ident" and "Functional Loc" shall be disabled
     *   and "Material number" shall be mandatory.
     *
     */
    private setReadOnlyOptions(): void {
        let readOnlyTechIdentNumber: boolean;
        let readOnlyFunctionalLoc: boolean;
        let readOnlyMaterial: boolean;
        let readOnlySerialNo: boolean;

        if (this.functionalLoc.value || this.techIdentNumber.value || this.material.value || this.serialNo.value) {
            readOnlyTechIdentNumber = !!(this.functionalLoc.value || this.material.value || this.serialNo.value);
            readOnlyFunctionalLoc = !!(this.techIdentNumber.value || this.material.value || this.serialNo.value);
            readOnlyMaterial = !!(this.techIdentNumber.value || this.functionalLoc.value);
            readOnlySerialNo = !!(this.techIdentNumber.value || this.functionalLoc.value);
        } else {
            readOnlyTechIdentNumber = false;
            readOnlyFunctionalLoc = false;
            readOnlyMaterial = false;
            readOnlySerialNo = false;
        }

        this.techIdentNumber.options = {
            ...this.techIdentNumber.options,
            readonly: readOnlyTechIdentNumber,
        };
        this.functionalLoc.options = {
            ...this.functionalLoc.options,
            readonly: readOnlyFunctionalLoc,
        };
        this.material.options = {
            ...this.material.options,
            readonly: readOnlyMaterial,
        };
        this.serialNo.options = {
            ...this.serialNo.options,
            readonly: readOnlySerialNo,
        };
    }

    public getOutModel(): ODataCseSalesTidInputOutModel {
        // create outmodel
        const outModel = <ODataCseSalesTidInputOutModel>super.getOutModel();
        outModel.TechnicalIdentNo = outModel.TechnicalIdentNo.toUpperCase();

        return outModel;
    }
}

export interface ODataCseSalesTidInputOutModel extends ODataObject {
    FrontId: string;
    TechnicalIdentNo: string;
    FunctionalLoc: string;
    Material: string;
    SerialNo: string;
    FrontDate: string;
    FrontTime: string;
    EquipmentNum: string;
    EmailFaxSet: [ODataValue];
    NotiCreateEquipmentsSet: [ODataValue];
}

export interface ODataCseSalesTidInput {
    NotificationNo: string;
    FrontId: string;
    TechnicalIdentNo: string;
    FunctionalLoc: string;
    Material: string;
    SerialNo: string;
    FrontDate: string;
    FrontTime: string;
    UTCDiff: string;
    UTCSign: string;
    EquipmentNum: string;
    EmailFaxSet: { results: ODataEmailFaxSet[] };
    NotiCreateEquipmentsSet: { results: ODataNotiCreateEquipmentsSet[] };
}

export interface ODataEmailFaxSet {
    FaxEmail: string;
    CommCategory: string;
}
export interface ODataNotiCreateEquipmentsSet {
    TechIdentNo: string;
    ManufPartNo: string;
    Equipment: string;
    Material: string;
    SerialNo: string;
    WarrantyEndDate: string;
    Division: string;
    Description: string;
    FunctionalLoc: string;
}
